import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './IntegracionesEmpresarialesPage.css';

const IntegracionesEmpresarialesPage = ({ darkMode, toggleDarkMode }) => {
  const beneficios = [
    {
      icon: '⚡',
      titulo: 'Eliminación de Tareas Manuales',
      descripcion: 'Elimina la necesidad de copiar datos manualmente entre sistemas, ahorrando horas diarias de trabajo.'
    },
    {
      icon: '✅',
      titulo: 'Datos Consistentes',
      descripcion: 'Un solo punto de verdad. Los datos se sincronizan automáticamente, eliminando discrepancias entre sistemas.'
    },
    {
      icon: '💰',
      titulo: 'Reducción de Errores',
      descripcion: 'Elimina errores humanos al transferir información entre sistemas, reduciendo costos por errores.'
    },
    {
      icon: '📊',
      titulo: 'Visibilidad Completa',
      descripcion: 'Acceso a toda la información de tu negocio desde cualquier sistema integrado, sin duplicar datos.'
    },
    {
      icon: '🔄',
      titulo: 'Sincronización en Tiempo Real',
      descripcion: 'Los datos se actualizan automáticamente en todos los sistemas conectados, siempre actualizados.'
    },
    {
      icon: '📈',
      titulo: 'Mejor Toma de Decisiones',
      descripcion: 'Información completa y actualizada en tiempo real para tomar decisiones más informadas y rápidas.'
    }
  ];

  const tiposIntegraciones = [
    {
      titulo: 'Integración ERP con E-commerce',
      descripcion: 'Conecta tu tienda online con tu ERP para sincronizar productos, inventario, pedidos y facturación automáticamente.',
      casos: [
        'Sincronización de catálogo de productos',
        'Actualización de stock en tiempo real',
        'Importación automática de pedidos',
        'Facturación automática desde pedidos online'
      ]
    },
    {
      titulo: 'Integración con Sistemas Contables',
      descripcion: 'Conecta tu ERP o sistema de facturación con tu software contable para evitar doble registro manual.',
      casos: [
        'Envío automático de facturas a contabilidad',
        'Sincronización de asientos contables',
        'Integración con A3, ContaPlus, Sage',
        'Conciliación bancaria automática'
      ]
    },
    {
      titulo: 'Integración con Almacenes y Logística',
      descripcion: 'Conecta tu sistema de gestión con almacenes externos o sistemas de logística para gestión unificada.',
      casos: [
        'Sincronización de inventarios',
        'Gestión de pedidos y envíos',
        'Integración con transportistas',
        'Tracking de envíos en tiempo real'
      ]
    },
    {
      titulo: 'Integración con Pasarelas de Pago',
      descripcion: 'Conecta tus sistemas con pasarelas de pago para procesamiento automático y conciliación.',
      casos: [
        'Procesamiento automático de pagos',
        'Conciliación de transacciones',
        'Integración con Stripe, PayPal, Redsys',
        'Notificaciones automáticas de pagos'
      ]
    },
    {
      titulo: 'Integración con RRHH y Nóminas',
      descripcion: 'Conecta tu ERP con sistemas de RRHH para sincronizar empleados, ausencias y nóminas.',
      casos: [
        'Sincronización de datos de empleados',
        'Integración con sistemas de nóminas',
        'Gestión de ausencias y vacaciones',
        'Reportes unificados de RRHH'
      ]
    },
    {
      titulo: 'Integración con CRM y Marketing',
      descripcion: 'Conecta tu CRM con sistemas de marketing, email marketing y redes sociales para automatizar campañas.',
      casos: [
        'Sincronización de contactos y leads',
        'Integración con Mailchimp, HubSpot',
        'Automatización de campañas',
        'Análisis unificado de marketing'
      ]
    },
    {
      titulo: 'Integración con Plataformas Cloud',
      descripcion: 'Conecta tus sistemas con servicios cloud como Google Workspace, Microsoft 365, Dropbox para sincronización.',
      casos: [
        'Sincronización con Google Drive',
        'Integración con Microsoft 365',
        'Backup automático en la nube',
        'Colaboración en documentos'
      ]
    },
    {
      titulo: 'Integraciones Personalizadas',
      descripcion: 'Desarrollamos integraciones a medida para sistemas específicos de tu sector o procesos únicos.',
      casos: [
        'APIs personalizadas',
        'Conectores específicos del sector',
        'Integraciones con sistemas legacy',
        'Soluciones a medida'
      ]
    }
  ];

  const tecnologias = [
    {
      nombre: 'APIs REST',
      descripcion: 'Integraciones mediante APIs REST estándar para conectar sistemas modernos de forma segura y eficiente.'
    },
    {
      nombre: 'Webhooks',
      descripcion: 'Notificaciones en tiempo real cuando ocurren eventos en sistemas externos, permitiendo reacciones inmediatas.'
    },
    {
      nombre: 'Conectores Predefinidos',
      descripcion: 'Utilizamos conectores predefinidos de plataformas como Make, Zapier, n8n para integraciones rápidas.'
    },
    {
      nombre: 'Desarrollo Personalizado',
      descripcion: 'Desarrollamos integraciones personalizadas cuando no existen conectores o requieren lógica específica.'
    }
  ];

  const proceso = [
    {
      paso: '1',
      titulo: 'Análisis de Sistemas',
      descripcion: 'Analizamos tus sistemas actuales, identificamos puntos de integración y definimos el flujo de datos.'
    },
    {
      paso: '2',
      titulo: 'Diseño de Integración',
      descripcion: 'Diseñamos la arquitectura de integración, elegimos la tecnología y definimos el formato de datos.'
    },
    {
      paso: '3',
      titulo: 'Desarrollo e Implementación',
      descripcion: 'Desarrollamos la integración, realizamos pruebas y la implementamos en tu entorno.'
    },
    {
      paso: '4',
      titulo: 'Pruebas y Validación',
      descripcion: 'Realizamos pruebas exhaustivas para asegurar que los datos se sincronizan correctamente entre sistemas.'
    },
    {
      paso: '5',
      titulo: 'Monitoreo y Mantenimiento',
      descripcion: 'Monitoreamos la integración, resolvemos incidencias y realizamos mejoras continuas.'
    }
  ];

  const sectores = [
    {
      titulo: 'Retail y E-commerce',
      descripcion: 'Integración de tiendas online con ERP, sistemas de inventario, pasarelas de pago y transportistas.'
    },
    {
      titulo: 'Manufactura',
      descripcion: 'Integración de sistemas de producción con ERP, control de calidad, almacenes y logística.'
    },
    {
      titulo: 'Servicios Profesionales',
      descripcion: 'Integración de sistemas de gestión con contabilidad, RRHH, CRM y herramientas de facturación.'
    },
    {
      titulo: 'Hostelería',
      descripcion: 'Integración de sistemas de reservas con ERP, TPV, sistemas de habitaciones y proveedores.'
    },
    {
      titulo: 'Logística',
      descripcion: 'Integración de sistemas de gestión con transportistas, almacenes, tracking y facturación.'
    },
    {
      titulo: 'Salud',
      descripcion: 'Integración de sistemas de gestión con historiales médicos, facturación y sistemas de citas.'
    }
  ];

  return (
    <>
      <SEO 
        title="Integraciones empresariales | Conectores y APIs | E-ASY"
        description="Conectores, APIs y sincronización entre ERP, e-commerce, contabilidad y almacenes. Integraciones para empresas de toda España, desde la sede en Tenerife."
        keywords="integraciones empresariales, integración sistemas, conectores sistemas, APIs empresas, integración ERP ecommerce, integración contabilidad, sincronización datos, integración sistemas Canarias, APIs Tenerife, conectores personalizados, integración sistemas España"
        canonical="https://e-asy.es/servicios/integraciones-empresariales"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`integraciones-empresariales-page ${darkMode ? 'dark' : 'light'}`}>
        
        {/* Hero Section */}
        <section className="integraciones-hero">
          <div className="integraciones-hero-content">
            <div className="integraciones-hero-text">
              <h1 className="integraciones-hero-title">Integraciones Empresariales</h1>
              <p className="integraciones-hero-subtitle">
                E-ASY desarrolla <strong>integraciones empresariales</strong> para conectar tus sistemas y automatizar 
                el flujo de datos. <strong>APIs</strong>, <strong>conectores</strong> y <strong>sincronización automática</strong> 
                entre ERP, e-commerce, contabilidad, almacenes y más.
              </p>
              <p className="integraciones-hero-subtitle">
                Elimina la necesidad de copiar datos manualmente entre sistemas. Conectamos tus sistemas para que trabajen 
                de forma integrada y automática. Desde <strong>Canarias</strong> desarrollamos integraciones para empresas 
                en toda <strong>España</strong> y proyectos <strong>internacionales</strong>.
              </p>
              <p className="integraciones-hero-subtitle">
                Si lo que necesitáis es llevar un <strong>prototipo de IA</strong> a
                producción (backend, APIs, Azure), eso está en{' '}
                <Link to="/servicios/ia-en-produccion">IA en producción</Link>
                : absorbe la integración de herramientas y APIs cuando el caso es un
                agente o una automatización con IA.
              </p>
              <div className="integraciones-hero-cta">
                <Link to="/contacto" className="integraciones-hero-button primary">
                  Solicita una consultoría gratuita
                </Link>
                <Link to="/servicios" className="integraciones-hero-button secondary">
                  Ver todos los servicios
                </Link>
              </div>
            </div>
            <div className="integraciones-hero-visual">
              <div className="integraciones-hero-graphic">
                <div className="integration-animation">
                  <div className="system-box system-1">
                    <div className="system-icon">📊</div>
                    <div className="system-label">ERP</div>
                  </div>
                  <div className="connection-line conn-1"></div>
                  <div className="connection-node node-1"></div>
                  <div className="connection-line conn-2"></div>
                  <div className="system-box system-2">
                    <div className="system-icon">🛒</div>
                    <div className="system-label">E-commerce</div>
                  </div>
                  <div className="connection-line conn-3"></div>
                  <div className="connection-node node-2"></div>
                  <div className="connection-line conn-4"></div>
                  <div className="system-box system-3">
                    <div className="system-icon">💰</div>
                    <div className="system-label">Contabilidad</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Qué es Section */}
        <section className="integraciones-que-es">
          <div className="integraciones-que-es-content">
            <div className="integraciones-que-es-header">
              <h2 className="integraciones-que-es-title">¿Qué son las Integraciones Empresariales?</h2>
              <p className="integraciones-que-es-subtitle">
                Las <strong>integraciones empresariales</strong> conectan diferentes sistemas de software para que compartan 
                información automáticamente. En lugar de copiar datos manualmente entre sistemas, las integraciones permiten 
                que los datos fluyan automáticamente entre ellos.
              </p>
              <p className="integraciones-que-es-subtitle">
                Si tu empresa usa múltiples sistemas (ERP, e-commerce, contabilidad, almacén, CRM) y tu equipo dedica tiempo 
                a copiar datos entre ellos, las <strong>integraciones</strong> automatizan este proceso, eliminan errores y 
                garantizan que todos los sistemas tengan información actualizada y consistente.
              </p>
            </div>
          </div>
        </section>

        {/* Beneficios Section */}
        <section className="integraciones-beneficios">
          <div className="integraciones-beneficios-content">
            <div className="integraciones-beneficios-header">
              <h2 className="integraciones-beneficios-title">Beneficios de las Integraciones Empresariales</h2>
              <p className="integraciones-beneficios-subtitle">
                Ventajas de conectar tus sistemas y automatizar el flujo de datos
              </p>
            </div>
            <div className="integraciones-beneficios-grid">
              {beneficios.map((beneficio, index) => (
                <div key={index} className="integraciones-beneficio-card">
                  <div className="beneficio-icon">{beneficio.icon}</div>
                  <h3 className="beneficio-title">{beneficio.titulo}</h3>
                  <p className="beneficio-description">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tipos de Integraciones Section */}
        <section className="integraciones-tipos">
          <div className="integraciones-tipos-content">
            <div className="integraciones-tipos-header">
              <h2 className="integraciones-tipos-title">Tipos de Integraciones que Desarrollamos</h2>
              <p className="integraciones-tipos-subtitle">
                Conectamos tus sistemas para que trabajen de forma integrada y automática
              </p>
            </div>
            <div className="integraciones-tipos-grid">
              {tiposIntegraciones.map((tipo, index) => (
                <div key={index} className="integraciones-tipo-card">
                  <h3 className="tipo-title">{tipo.titulo}</h3>
                  <p className="tipo-description">{tipo.descripcion}</p>
                  <div className="tipo-casos">
                    <h4 className="casos-title">Casos de uso:</h4>
                    <ul className="casos-list">
                      {tipo.casos.map((caso, idx) => (
                        <li key={idx} className="caso-item">{caso}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tecnologías Section */}
        <section className="integraciones-tecnologias">
          <div className="integraciones-tecnologias-content">
            <div className="integraciones-tecnologias-header">
              <h2 className="integraciones-tecnologias-title">Tecnologías de Integración</h2>
              <p className="integraciones-tecnologias-subtitle">
                Utilizamos las mejores tecnologías y metodologías para desarrollar integraciones robustas y escalables
              </p>
            </div>
            <div className="integraciones-tecnologias-grid">
              {tecnologias.map((tecnologia, index) => (
                <div key={index} className="integraciones-tecnologia-card">
                  <h3 className="tecnologia-title">{tecnologia.nombre}</h3>
                  <p className="tecnologia-description">{tecnologia.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proceso Section */}
        <section className="integraciones-proceso">
          <div className="integraciones-proceso-content">
            <div className="integraciones-proceso-header">
              <h2 className="integraciones-proceso-title">Nuestro Proceso de Integración</h2>
              <p className="integraciones-proceso-subtitle">
                Metodología probada que asegura el éxito de tu proyecto de integración
              </p>
            </div>
            <div className="integraciones-proceso-timeline">
              {proceso.map((item, index) => (
                <div key={index} className="proceso-step">
                  <div className="step-number">{item.paso}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.titulo}</h3>
                    <p className="step-description">{item.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sectores Section */}
        <section className="integraciones-sectores">
          <div className="integraciones-sectores-content">
            <div className="integraciones-sectores-header">
              <h2 className="integraciones-sectores-title">Integraciones para Diferentes Sectores</h2>
              <p className="integraciones-sectores-subtitle">
                Adaptamos las integraciones a las necesidades específicas de cada sector
              </p>
            </div>
            <div className="integraciones-sectores-grid">
              {sectores.map((sector, index) => (
                <div key={index} className="integraciones-sector-card">
                  <h3 className="sector-title">{sector.titulo}</h3>
                  <p className="sector-description">{sector.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="integraciones-cta">
          <div className="integraciones-cta-content">
            <h2 className="integraciones-cta-title">¿Necesitas Integrar tus Sistemas?</h2>
            <p className="integraciones-cta-subtitle">
              Agenda una consultoría gratuita y descubre cómo podemos conectar tus sistemas para automatizar el flujo de datos
            </p>
            <div className="integraciones-cta-buttons">
              <Link to="/contacto" className="integraciones-cta-button primary">
                Solicita una consultoría gratuita
              </Link>
              <Link to="/servicios" className="integraciones-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>

      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default IntegracionesEmpresarialesPage;






